// HeroUnified — warm, holistic opening that speaks to BOTH ends of life
// (the elders we are letting go of, the newborns we are just meeting),
// before the visitor decides which demo to explore. Replaces the previous
// elder-only Hero on the unified main landing.
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { FmnWatermark } from "../FmnWatermark";

const FAMILY_TABLE_IMG = "/family-at-table.png";

export function HeroUnified() {
    const { t } = useTranslation();
    return (
        <section
            className="relative min-h-[92vh] flex items-center px-6 pt-80 pb-20 rem-watercolor-bg overflow-hidden"
            data-testid="hero-unified-section"
        >
            <FmnWatermark position="bottom-left" size={760} opacity={0.05} rotate={10} />
            {/* Soft floating petals */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-[18%] left-[6%] opacity-35 animate-float-gentle">
                    <ForgetMeNotIcon size={48} />
                </div>
                <div className="absolute top-[26%] right-[10%] opacity-30 animate-float-gentle" style={{ animationDelay: "1.6s" }}>
                    <ForgetMeNotIcon size={36} />
                </div>
                <div className="absolute bottom-[14%] left-[16%] opacity-25 animate-float-gentle" style={{ animationDelay: "2.4s" }}>
                    <ForgetMeNotIcon size={40} />
                </div>
                <div className="absolute bottom-[22%] right-[12%] opacity-30 animate-float-gentle" style={{ animationDelay: "0.9s" }}>
                    <ForgetMeNotIcon size={52} />
                </div>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start w-full">
                {/* Left column — copy */}
                <div className="lg:col-span-7">
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="font-sans text-xs uppercase tracking-[0.32em] text-[#6B7DB8] font-semibold mb-7"
                        data-testid="hero-unified-eyebrow"
                    >
                        {t("unified_hero.eyebrow")}
                    </motion.p>

                    <motion.h1
                        initial={{ opacity: 0, y: 22 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1.0, delay: 0.22 }}
                        className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2B2A] leading-[1.06] tracking-tight"
                        data-testid="hero-unified-headline"
                    >
                        {t("unified_hero.headline_part1")}{" "}
                        <em className="italic text-[#4A5A95] block sm:inline">
                            {t("unified_hero.headline_emphasis")}
                        </em>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.45 }}
                        className="mt-8 max-w-xl text-lg sm:text-xl text-[#5C5A59] font-light leading-relaxed"
                        data-testid="hero-unified-lead"
                    >
                        {t("unified_hero.lead")}
                    </motion.p>

                    {/* The two-version pair — visually together */}
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.65 }}
                        className="mt-10 grid sm:grid-cols-2 gap-5 max-w-xl"
                    >
                        <div className="rounded-2xl bg-white/65 backdrop-blur border border-[#8C9ECB]/30 p-5">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="w-2 h-2 rounded-full bg-[#6B7DB8]" />
                                <span className="text-[10px] uppercase tracking-[0.24em] text-[#6B7DB8] font-semibold">For the elders</span>
                            </div>
                            <p className="text-sm text-[#2D2B2A] font-serif italic leading-snug">
                                {t("unified_hero.for_elders")}
                            </p>
                        </div>
                        <div className="rounded-2xl bg-white/65 backdrop-blur border border-[#F4C7CC]/40 p-5">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="w-2 h-2 rounded-full bg-[#A85D62]" />
                                <span className="text-[10px] uppercase tracking-[0.24em] text-[#A85D62] font-semibold">For the newborns</span>
                            </div>
                            <p className="text-sm text-[#2D2B2A] font-serif italic leading-snug">
                                {t("unified_hero.for_kids")}
                            </p>
                        </div>
                    </motion.div>

                    <motion.a
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.9, delay: 0.95 }}
                        href="#story"
                        className="mt-10 inline-block text-sm uppercase tracking-[0.26em] text-[#6B7DB8] hover:text-[#4A5A95] font-semibold border-b border-[#6B7DB8]/30 hover:border-[#4A5A95] pb-1 transition-colors"
                        data-testid="hero-unified-scroll-cue"
                    >
                        {t("unified_hero.scroll_cue")}
                    </motion.a>
                </div>

                {/* Right column — paired family scenes: elder + newborn */}
                <motion.div
                    initial={{ opacity: 0, x: 26 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    className="lg:col-span-5 relative space-y-5"
                    data-testid="hero-unified-images"
                >
                    <div className="relative rounded-[2rem] overflow-hidden shadow-rem-card border border-white/60" data-testid="hero-image-elder">
                        <img
                            src={FAMILY_TABLE_IMG}
                            alt="A family gathered around a kitchen table with their grandmother, sharing photos through a lavender remembr tablet"
                            className="w-full h-auto object-cover"
                            loading="eager"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1c25]/15 via-transparent to-transparent pointer-events-none" />
                        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/85 backdrop-blur text-[10px] font-semibold uppercase tracking-[0.22em] text-[#4A5A95]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#6B7DB8]" />
                            For the elders
                        </div>
                    </div>
                    <div className="relative rounded-[2rem] overflow-hidden shadow-rem-card border border-white/60" data-testid="hero-image-newborn">
                        <img
                            src="/family-with-newborn.png"
                            alt="A family welcoming a newborn baby home, gathered around the mother on a sofa with the pink remembr tablet on the side-table"
                            className="w-full h-auto object-cover"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#A85D62]/20 via-transparent to-transparent pointer-events-none" />
                        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/85 backdrop-blur text-[10px] font-semibold uppercase tracking-[0.22em] text-[#A85D62]">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#A85D62]" />
                            For the newborns
                        </div>
                    </div>
                    <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-14 h-14 rounded-full bg-white/95 backdrop-blur border border-white/70 shadow-rem-soft items-center justify-center z-10" data-testid="hero-images-bridge">
                        <ForgetMeNotIcon size={26} />
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
